import { useState, useEffect } from "react";
import { Card, CardContent, CardHeader, CardTitle } from "../components/ui/card";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "../components/ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "../components/ui/table";
import { Badge } from "../components/ui/badge";
import { Textarea } from "../components/ui/textarea";
import { toast } from "sonner";
import { Plus, Search, Home, Edit, Trash2, MapPin, Users, Euro, Building2, UserPlus, UserMinus, Loader2 } from "lucide-react";
import api from "../lib/api";
import { format } from "date-fns";
import { de } from "date-fns/locale";
import { useAuth } from "../context/AuthContext";

const BRAND_COLOR = "#c11718";

export default function WohnungenPage() {
  const { user } = useAuth();
  const [wohnungen, setWohnungen] = useState([]);
  const [guests, setGuests] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [dialogOpen, setDialogOpen] = useState(false);
  const [editingWohnung, setEditingWohnung] = useState(null);
  const [assignDialogOpen, setAssignDialogOpen] = useState(false);
  const [selectedWohnung, setSelectedWohnung] = useState(null);
  const [selectedGuestId, setSelectedGuestId] = useState("");
  const [assigning, setAssigning] = useState(false);
  const [formData, setFormData] = useState({
    name: "",
    address: "",
    city: "",
    postal_code: "",
    floor: 0,
    total_rooms: 1,
    total_beds: 1,
    price_per_month: 0,
    notes: ""
  });

  const isAdmin = user?.role === "admin";

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [wohnungenRes, guestsRes] = await Promise.all([
        api.get("/wohnungen"),
        api.get("/guests")
      ]);
      setWohnungen(wohnungenRes.data);
      setGuests(guestsRes.data.filter(g => !g.archived && g.status === "active"));
    } catch (error) {
      toast.error("Fehler beim Laden der Daten");
    } finally {
      setLoading(false);
    }
  };

  const resetForm = () => {
    setFormData({
      name: "",
      address: "",
      city: "",
      postal_code: "",
      floor: 0,
      total_rooms: 1,
      total_beds: 1,
      price_per_month: 0,
      notes: ""
    });
    setEditingWohnung(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      if (editingWohnung) {
        await api.put(`/wohnungen/${editingWohnung.id}`, formData);
        toast.success("Wohnung aktualisiert");
      } else {
        await api.post("/wohnungen", formData);
        toast.success("Wohnung erstellt");
      }
      setDialogOpen(false);
      resetForm();
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Speichern");
    }
  };

  const handleEdit = (wohnung) => {
    setEditingWohnung(wohnung);
    setFormData({
      name: wohnung.name,
      address: wohnung.address,
      city: wohnung.city || "",
      postal_code: wohnung.postal_code || "",
      floor: wohnung.floor || 0,
      total_rooms: wohnung.total_rooms || 1,
      total_beds: wohnung.total_beds || 1,
      price_per_month: wohnung.price_per_month || 0,
      notes: wohnung.notes || ""
    });
    setDialogOpen(true);
  };

  const handleDelete = async (id) => {
    if (!confirm("Möchten Sie diese Wohnung wirklich löschen?")) return;
    try {
      await api.delete(`/wohnungen/${id}`);
      toast.success("Wohnung gelöscht");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler beim Löschen");
    }
  };

  const openAssignDialog = (wohnung) => {
    setSelectedWohnung(wohnung);
    setSelectedGuestId("");
    setAssignDialogOpen(true);
  };

  const handleAssignTenant = async () => {
    if (!selectedWohnung || !selectedGuestId) return;
    setAssigning(true);
    try {
      await api.post(`/wohnungen/${selectedWohnung.id}/assign-tenant?guest_id=${selectedGuestId}`);
      toast.success("Mieter zugewiesen");
      setAssignDialogOpen(false);
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    } finally {
      setAssigning(false);
    }
  };

  const handleRemoveTenant = async (wohnungId) => {
    if (!confirm("Möchten Sie den Mieter wirklich entfernen?")) return;
    try {
      await api.post(`/wohnungen/${wohnungId}/remove-tenant`);
      toast.success("Mieter entfernt");
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fehler");
    }
  };

  const filteredWohnungen = wohnungen.filter(w =>
    w.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.address.toLowerCase().includes(searchTerm.toLowerCase()) ||
    w.current_tenant_name?.toLowerCase().includes(searchTerm.toLowerCase())
  );

  const stats = {
    total: wohnungen.length,
    occupied: wohnungen.filter(w => w.current_tenant_id).length,
    available: wohnungen.filter(w => !w.current_tenant_id).length
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-slate-900"></div>
      </div>
    );
  }

  return (
    <div className="space-y-4 sm:space-y-6 p-4 sm:p-6" data-testid="wohnungen-page">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-3">
        <div>
          <h1 className="text-xl sm:text-2xl font-bold tracking-tight text-slate-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Wohnungen
          </h1>
          <p className="text-sm text-slate-500 mt-1">Wohnungsverwaltung</p>
        </div>
        {isAdmin && (
          <Dialog open={dialogOpen} onOpenChange={(open) => { setDialogOpen(open); if (!open) resetForm(); }}>
            <DialogTrigger asChild>
              <Button style={{ backgroundColor: BRAND_COLOR }} data-testid="add-wohnung-btn">
                <Plus className="h-4 w-4 mr-2" />
                Neue Wohnung
              </Button>
            </DialogTrigger>
            <DialogContent className="max-w-lg">
              <DialogHeader>
                <DialogTitle className="flex items-center gap-2">
                  <Home className="h-5 w-5" style={{ color: BRAND_COLOR }} />
                  {editingWohnung ? "Wohnung bearbeiten" : "Neue Wohnung"}
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleSubmit} className="space-y-4">
                <div className="grid grid-cols-2 gap-3">
                  <div className="col-span-2 space-y-2">
                    <Label>Name / Bezeichnung *</Label>
                    <Input
                      value={formData.name}
                      onChange={(e) => setFormData(prev => ({ ...prev, name: e.target.value }))}
                      placeholder="z.B. Wohnung 1A"
                      required
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Adresse *</Label>
                    <Input
                      value={formData.address}
                      onChange={(e) => setFormData(prev => ({ ...prev, address: e.target.value }))}
                      placeholder="Straße und Hausnummer"
                      required
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>PLZ</Label>
                    <Input
                      value={formData.postal_code}
                      onChange={(e) => setFormData(prev => ({ ...prev, postal_code: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Stadt</Label>
                    <Input
                      value={formData.city}
                      onChange={(e) => setFormData(prev => ({ ...prev, city: e.target.value }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Etage</Label>
                    <Input
                      type="number"
                      value={formData.floor}
                      onChange={(e) => setFormData(prev => ({ ...prev, floor: parseInt(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Miete (€/Monat)</Label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.price_per_month}
                      onChange={(e) => setFormData(prev => ({ ...prev, price_per_month: parseFloat(e.target.value) || 0 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Zimmer</Label>
                    <Input
                      type="number"
                      value={formData.total_rooms}
                      onChange={(e) => setFormData(prev => ({ ...prev, total_rooms: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div className="space-y-2">
                    <Label>Betten</Label>
                    <Input
                      type="number"
                      value={formData.total_beds}
                      onChange={(e) => setFormData(prev => ({ ...prev, total_beds: parseInt(e.target.value) || 1 }))}
                    />
                  </div>
                  <div className="col-span-2 space-y-2">
                    <Label>Notizen</Label>
                    <Textarea
                      value={formData.notes}
                      onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                      placeholder="Zusätzliche Informationen..."
                    />
                  </div>
                </div>
                <Button type="submit" className="w-full" style={{ backgroundColor: BRAND_COLOR }}>
                  {editingWohnung ? "Speichern" : "Erstellen"}
                </Button>
              </form>
            </DialogContent>
          </Dialog>
        )}
      </div>

      {/* Search */}
      <div className="relative max-w-md">
        <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-slate-400" />
        <Input
          placeholder="Wohnung oder Mieter suchen..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10"
        />
      </div>

      {/* Stats */}
      <div className="grid grid-cols-3 gap-3">
        <Card className="border-slate-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-slate-100 rounded-lg">
                <Building2 className="h-5 w-5 text-slate-600" />
              </div>
              <div>
                <p className="text-xs text-slate-500">Gesamt</p>
                <p className="text-xl font-bold">{stats.total}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-emerald-200 bg-emerald-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-emerald-100 rounded-lg">
                <Users className="h-5 w-5 text-emerald-600" />
              </div>
              <div>
                <p className="text-xs text-emerald-600">Vermietet</p>
                <p className="text-xl font-bold text-emerald-700">{stats.occupied}</p>
              </div>
            </div>
          </CardContent>
        </Card>
        <Card className="border-blue-200 bg-blue-50/50">
          <CardContent className="p-4">
            <div className="flex items-center gap-3">
              <div className="p-2 bg-blue-100 rounded-lg">
                <Home className="h-5 w-5 text-blue-600" />
              </div>
              <div>
                <p className="text-xs text-blue-600">Verfügbar</p>
                <p className="text-xl font-bold text-blue-700">{stats.available}</p>
              </div>
            </div>
          </CardContent>
        </Card>
      </div>

      {/* Table */}
      {filteredWohnungen.length > 0 ? (
        <Card className="border-slate-200">
          <CardContent className="p-0">
            <div className="overflow-x-auto">
              <Table>
                <TableHeader>
                  <TableRow className="bg-slate-50">
                    <TableHead>Wohnung</TableHead>
                    <TableHead>Adresse</TableHead>
                    <TableHead>Zimmer/Betten</TableHead>
                    <TableHead>Miete</TableHead>
                    <TableHead>Mieter</TableHead>
                    {isAdmin && <TableHead className="text-right">Aktionen</TableHead>}
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {filteredWohnungen.map((wohnung) => (
                    <TableRow key={wohnung.id}>
                      <TableCell className="font-medium">
                        <div className="flex items-center gap-2">
                          <Home className="h-4 w-4 text-slate-400" />
                          {wohnung.name}
                        </div>
                        {wohnung.floor > 0 && (
                          <span className="text-xs text-slate-500 ml-6">{wohnung.floor}. Etage</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm text-slate-600">
                          <MapPin className="h-3 w-3" />
                          {wohnung.address}
                        </div>
                        {(wohnung.postal_code || wohnung.city) && (
                          <span className="text-xs text-slate-400 ml-4">{wohnung.postal_code} {wohnung.city}</span>
                        )}
                      </TableCell>
                      <TableCell>
                        <span className="text-sm">{wohnung.total_rooms} Zi. / {wohnung.total_beds} Bett</span>
                      </TableCell>
                      <TableCell>
                        <span className="font-medium">€{wohnung.price_per_month?.toFixed(2)}</span>
                      </TableCell>
                      <TableCell>
                        {wohnung.current_tenant_name ? (
                          <div className="flex items-center gap-2">
                            <Badge className="bg-emerald-100 text-emerald-700">{wohnung.current_tenant_name}</Badge>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-red-500"
                                onClick={() => handleRemoveTenant(wohnung.id)}
                                title="Mieter entfernen"
                              >
                                <UserMinus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        ) : (
                          <div className="flex items-center gap-2">
                            <Badge variant="outline" className="text-slate-500">Leer</Badge>
                            {isAdmin && (
                              <Button
                                variant="ghost"
                                size="sm"
                                className="h-6 w-6 p-0 text-blue-600"
                                onClick={() => openAssignDialog(wohnung)}
                                title="Mieter zuweisen"
                              >
                                <UserPlus className="h-3 w-3" />
                              </Button>
                            )}
                          </div>
                        )}
                      </TableCell>
                      {isAdmin && (
                        <TableCell className="text-right">
                          <div className="flex justify-end gap-1">
                            <Button variant="ghost" size="sm" onClick={() => handleEdit(wohnung)}>
                              <Edit className="h-4 w-4" />
                            </Button>
                            <Button variant="ghost" size="sm" className="text-red-600" onClick={() => handleDelete(wohnung.id)}>
                              <Trash2 className="h-4 w-4" />
                            </Button>
                          </div>
                        </TableCell>
                      )}
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </div>
          </CardContent>
        </Card>
      ) : (
        <Card className="border-slate-200">
          <CardContent className="py-12 text-center">
            <Home className="h-12 w-12 mx-auto text-slate-300 mb-3" />
            <p className="text-slate-500">
              {searchTerm ? "Keine Wohnungen gefunden" : "Keine Wohnungen vorhanden"}
            </p>
            {isAdmin && !searchTerm && (
              <Button variant="outline" className="mt-4" onClick={() => setDialogOpen(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Erste Wohnung erstellen
              </Button>
            )}
          </CardContent>
        </Card>
      )}

      {/* Assign Tenant Dialog */}
      <Dialog open={assignDialogOpen} onOpenChange={setAssignDialogOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <UserPlus className="h-5 w-5" style={{ color: BRAND_COLOR }} />
              Mieter zuweisen
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedWohnung && (
              <div className="p-3 bg-slate-50 rounded-lg">
                <p className="text-sm font-medium">{selectedWohnung.name}</p>
                <p className="text-xs text-slate-500">{selectedWohnung.address}</p>
              </div>
            )}
            <div className="space-y-2">
              <Label>Bewohner auswählen</Label>
              <Select value={selectedGuestId} onValueChange={setSelectedGuestId}>
                <SelectTrigger>
                  <SelectValue placeholder="Bewohner wählen..." />
                </SelectTrigger>
                <SelectContent>
                  {guests.map((guest) => (
                    <SelectItem key={guest.id} value={guest.id}>
                      {guest.first_name} {guest.last_name}
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="flex gap-3">
              <Button variant="outline" className="flex-1" onClick={() => setAssignDialogOpen(false)}>
                Abbrechen
              </Button>
              <Button
                className="flex-1"
                style={{ backgroundColor: BRAND_COLOR }}
                onClick={handleAssignTenant}
                disabled={assigning || !selectedGuestId}
              >
                {assigning ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <UserPlus className="h-4 w-4 mr-2" />}
                {assigning ? "Wird zugewiesen..." : "Zuweisen"}
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
